import { useContext, useEffect, useState } from "react";
import "./EcommerceCategorySidebar.scss";
import IGetSubCategoriesDTO from "../../../models/DTO/IGetSubCategoriesDTO";
import IOnGetCategoriesDTO from "../../../models/DTO/IOnGetCategoriesDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import ICategory from "../../../models/ICategory";
import IOrder from "../../../models/IOrder";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwTitle from "../../shared/OwTitle/OwTitle";
import CategoryContext from "../../../contexts/CategoryContext";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { SocketContext } from "../../../contexts/SocketContext";

type Props = {
    rootCategories: ICategory[];
    toggleSidebar: () => void;
    orderModel?: IOrder;
    hasNoOrder?: boolean;
    viewCategory: (category: ICategory) => void;
};

export const EcommerceCategorySidebar = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const socket = useContext(SocketContext)!;

    const categoryContext = useContext(CategoryContext);

    const [selectedParentCategory, setSelectedParentCategory] = useState<ICategory>();
    const [selectedCategories, setSelectedCategories] = useState<ICategory[]>(props.rootCategories);
    const [categoryHierarchy, setCategoryHierarchy] = useState<ICategory[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(false);

    const onGetCategories = (data: IOnGetCategoriesDTO) => {
        setSelectedCategories(data.categories);
        setLoadingCategories(false);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.CategoriesOnGetSubCategories, fn: onGetCategories }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        if (selectedParentCategory) {
            const payload: IGetSubCategoriesDTO = {
                parentCategoryGuid: selectedParentCategory.guid,
                userToken: userModel.token,
                userGuid: userModel.guid,
                supplierGuid: supplierModel.guid,
                vendorGuid: vendorModel.guid,
            };

            socket.emit(EndpointsEnum.CategoriesGetSubCategories, payload);

            setLoadingCategories(true);
        }
    }, [selectedParentCategory]);

    const goUpCategoryLevel = () => {
        if (selectedParentCategory && categoryHierarchy.length) {
            const existingCatHierarchy = [...categoryHierarchy];
            existingCatHierarchy.pop(); // Remove current.

            if (existingCatHierarchy.length > 0) {
                const newParentCategory = existingCatHierarchy[existingCatHierarchy.length - 1];
                setSelectedParentCategory(newParentCategory);
                setSelectedCategories([]); // Adjust accordingly if you're keeping children.
            } else {
                setSelectedCategories(props.rootCategories);
                categoryContext.setFn(undefined);
                setSelectedParentCategory(undefined);
            }

            setCategoryHierarchy(existingCatHierarchy);
        }
    };

    const selectCategory = (category: ICategory) => {
        if (category) {
            if (category.has_dependents) {
                const existingCatHierarchy = [...categoryHierarchy];

                existingCatHierarchy.push(category);
                setSelectedParentCategory(category);
                setCategoryHierarchy(existingCatHierarchy);
            } else {
                categoryContext.setFn(category);

                // go into the category in the order entry view.
                if (props.orderModel || props.hasNoOrder) {
                    props.viewCategory(category);
                }
            }
        }
    };

    return (
        <div className={"EcommerceCategorySidebar p-2"}>
            <div className={"d-flex justify-content-between align-items-center"}>
                <div className={"d-flex justify-content-center align-items-center"}>
                    <OwTitle
                        tag={"h3"}
                        title={"categories"}
                        rightIconName={loadingCategories ? "fas fa-spin fa-refresh text-warning" : ""}
                    />
                </div>

                <OwButton fn={props.toggleSidebar} centerIconClass={"fas fa-times"} className={"btn btn-sm btn-danger"} />
            </div>
            <div className={"mt-3"}>
                {selectedParentCategory ? (
                    <div className={"category-row"} onClick={goUpCategoryLevel}>
                        <OwLabel className={"pointer"} leftIconClass={"fas fa-arrow-left"} title={"back"} />
                    </div>
                ) : null}
                {selectedCategories
                    .filter((item) => !item.hide_category)
                    .map((category) => {
                        return (
                            <div key={category.guid} className={"category-row"} onClick={() => selectCategory(category)}>
                                {category.name}
                                {category.has_dependents ? (
                                    <div>
                                        <i className={"fas fa-chevron-right"} />
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};
